/**
 * Whether correcting a site's address should move its map pin.
 *
 * Locations already re-geocode on an address edit. Sites did not, and said so
 * in the edit sheet's own hint, so typing a corrected street address left the
 * pin pointing at the old place silently. That half is simply wrong.
 *
 * Copying the Location behaviour wholesale would be wrong the other way, and
 * destructive. A site with `capturedVia: 'gps'` has coordinates that are an
 * **observation**: somebody stood on the parcel and the device reported a fix
 * to within a few metres. Its address is *derived* from those coordinates by a
 * reverse geocode, and that derivation is thin. A pin on a real street can come
 * back with no house number and no city at all, which is exactly the case where
 * a user then types the address in by hand. Re-geocoding at that moment would
 * throw away the measurement in favour of a guess at a street centroid, and the
 * user would have no idea it happened.
 *
 * `resolveSitePosition` already states the principle for creation:
 *
 *   PROVIDED — the caller gave coordinates. Stored verbatim, never
 *   second-guessed. A GPS fix from a phone standing on the site is better than
 *   anything a geocoder will produce from its address.
 *
 * This applies the same principle to editing. The split is not "phone versus
 * desktop", it is **measured versus derived**: a pin somebody placed is worth
 * more than an address a geocoder will interpret, so moving it is the user's
 * call and not a silent side effect of fixing a typo.
 */

export type RegeocodeAction = 'none' | 'geocode' | 'confirm'

export interface RegeocodePlan {
	action: RegeocodeAction
	/** Why, for logging and for the tests to assert on rather than guessing. */
	reason:
		| 'address-unchanged'
		| 'no-address-to-geocode'
		| 'no-coordinates-to-lose'
		| 'coordinates-were-geocoded'
		| 'coordinates-were-captured'
	/** Present only when `action` is `confirm`: what the user stands to lose. */
	prompt?: string
}

export interface SiteProvenance {
	latitude?: number | string | null
	longitude?: number | string | null
	/** How the coordinates arrived: 'gps', 'map' or 'address'. */
	capturedVia?: string | null
	/** PENDING | SUCCESS | NOT_FOUND | ERROR | PROVIDED */
	geocodeStatus?: string | null
	/** Radius in metres the device reported, for a GPS fix. */
	gpsAccuracy?: number | null
}

/**
 * The address fields that decide where the pin sits.
 *
 * A location spells the same idea differently: `stateProvince` rather than
 * `state`, and it carries a `country` a site does not. So the field list is a
 * parameter rather than a constant, and the judgement below is shared instead
 * of copied. A second copy would drift, and the drift would show up as one
 * screen protecting a hand-placed pin and the other quietly overwriting it.
 */
export const SITE_SIGNATURE_FIELDS = ['address', 'city', 'state', 'postalCode'] as const

export const LOCATION_SIGNATURE_FIELDS = [
	'address',
	'city',
	'stateProvince',
	'postalCode',
	'country',
] as const

type SignatureFields = readonly string[]

export interface RegeocodeOptions {
	/** Which address fields decide where the pin sits. Defaults to a site's. */
	fields?: SignatureFields
	/**
	 * What to call the record in the prompt.
	 *
	 * A user reading "This site…" in a Location dialog learns that the message
	 * was written for something else, which is enough to make them distrust it
	 * and click through.
	 */
	noun?: string
}

/**
 * Comparing signatures rather than individual fields means a casing or
 * whitespace edit does not read as the site having moved, and so does not
 * spend a geocode or raise a prompt.
 */
export const siteAddressSignature = (
	v: Record<string, unknown> | null | undefined,
	fields: SignatureFields = SITE_SIGNATURE_FIELDS,
): string =>
	fields
		.map(key => {
			const value = v?.[key]
			return value == null ? '' : String(value).trim().toLowerCase()
		})
		.join('|')

/** The same fields as a free-text query for the geocoder. */
export const siteAddressQuery = (
	v: Record<string, unknown> | null | undefined,
	fields: SignatureFields = SITE_SIGNATURE_FIELDS,
): string =>
	fields
		.map(key => {
			const value = v?.[key]
			return value == null ? '' : String(value).trim()
		})
		.filter(Boolean)
		.join(', ')

const hasCoordinates = (site: SiteProvenance): boolean => {
	const lat = site.latitude
	const lng = site.longitude
	if (lat == null || lng == null || lat === '' || lng === '') return false
	return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
}

/**
 * True when the coordinates were placed by a person rather than derived from
 * an address.
 *
 * `capturedVia: 'address'` is NOT captured: it records that the coordinates
 * came from geocoding the address in the first place, so re-geocoding a
 * corrected address is precisely the right thing to do and needs no prompt.
 */
const wereCaptured = (site: SiteProvenance): boolean => {
	if (site.capturedVia === 'gps' || site.capturedVia === 'map') return true
	// Pre-MYD-408 sites have no capturedVia. PROVIDED still means the caller
	// supplied coordinates rather than the server deriving them, so it is the
	// best signal those older records carry.
	return site.geocodeStatus === 'PROVIDED'
}

/**
 * How accurate the fix was, in words, or null when there is nothing to quote.
 *
 * Only a GPS capture has an accuracy worth stating. A dragged pin is equally
 * deliberate and equally worth protecting, but "±8 m" would be a fiction.
 */
const accuracyPhrase = (site: SiteProvenance): string | null => {
	if (site.capturedVia !== 'gps') return null
	const metres = Number(site.gpsAccuracy)
	if (!Number.isFinite(metres) || metres <= 0) return null
	return `to within ${Math.round(metres)} m`
}

/**
 * What to do about the pin, given the address the user is saving.
 *
 * @param site Coordinates and their provenance, as stored.
 * @param previousSignature The address signature the current pin belongs to.
 * @param nextValues The address fields being saved.
 */
export const planSiteRegeocode = (
	site: SiteProvenance,
	previousSignature: string,
	nextValues: Record<string, unknown>,
	{ fields = SITE_SIGNATURE_FIELDS, noun = 'site' }: RegeocodeOptions = {},
): RegeocodePlan => {
	const nextSignature = siteAddressSignature(nextValues, fields)

	// Saving a name or a square footage must never spend a geocode, and must
	// never raise a prompt about a pin the user did not touch.
	if (nextSignature === previousSignature) {
		return { action: 'none', reason: 'address-unchanged' }
	}

	if (!siteAddressQuery(nextValues, fields)) {
		// Clearing the address is not a request to move the pin somewhere else.
		return { action: 'none', reason: 'no-address-to-geocode' }
	}

	if (!hasCoordinates(site)) {
		return { action: 'geocode', reason: 'no-coordinates-to-lose' }
	}

	if (!wereCaptured(site)) {
		return { action: 'geocode', reason: 'coordinates-were-geocoded' }
	}

	const accuracy = accuracyPhrase(site)
	const how =
		site.capturedVia === 'gps'
			? `This ${noun} was captured on-site${accuracy ? ` ${accuracy}` : ''}.`
			// "Placed by hand" rather than "placed by hand on the map": a site's
			// map capture really was dragged on a map, but a location's is typed
			// into coordinate inputs. Both are deliberate, which is the part that
			// matters, and only one of them involved a map.
			: `This ${noun}’s pin was placed by hand.`

	return {
		action: 'confirm',
		reason: 'coordinates-were-captured',
		prompt: `${how} Move the pin to the new address?`,
	}
}

export default planSiteRegeocode
